import React, { useState, useEffect } from "react";
import {
  TableCell,
  TableSortLabel as MuiTableSortLabel,
  Tooltip,
  Box,
  styled,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { paletteVar } from "@platform/design-system";

import { handleSort as utilsSort } from "../utils";

const TableSortLabel = styled(MuiTableSortLabel)(({ theme }) => {
  const hoverColor = paletteVar(
    "secondary-main",
    theme.palette.brandSupporting.medium,
  );
  return {
    color: theme.palette.text.primary,
    "&.Mui-active": {
      color: theme.palette.text.primary,
      "& .MuiTableSortLabel-icon": {
        color: theme.palette.text.primary,
      },
    },
    "&:hover": {
      color: hoverColor,
      "& .MuiTableSortLabel-icon": {
        color: hoverColor,
        opacity: 1,
      },
    },
  };
});

const TableHeadersSort = ({
  headers,
  data,
  setData,
  sortBy = "",
  setSortBy = () => {},
  isDescending = "asc",
  setIsDescending = () => {},
}) => {
  const isDescOrder =
    isDescending === true || isDescending === "desc" ? "desc" : "asc";

  const [orderBy, setOrderBy] = useState(
    sortBy || headers[0]?.id || headers[0]?.field,
  );
  const [order, setOrder] = useState(isDescOrder || "asc");

  // Sync local state with props when they change (e.g., tab switch, external sort changes)
  useEffect(() => {
    if (sortBy) {
      setOrderBy(sortBy);
    }
  }, [sortBy]);

  useEffect(() => {
    const newOrder =
      isDescending === true || isDescending === "desc" ? "desc" : "asc";
    setOrder(newOrder);
  }, [isDescending]);

  const handleSort = (id, objectType, onNull) => {
    setOrderBy(id);
    setSortBy(id);
    const isAsc = orderBy === id && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    // Convert order string to boolean for setIsDescending
    const isDescBool = newOrder === "desc";
    setIsDescending(isDescBool);
    // Only do client-side sorting if setData is provided
    // For server-side sorting (main RFPs page), setData should not be called
    if (setData && typeof setData === "function") {
      setData([...utilsSort(data, newOrder, id, objectType, onNull)]);
    }
  };

  return headers?.map(
    ({
      id,
      label,
      isSortable,
      objectType,
      tooltip,
      onNull,
      width = null,

      // Bridge for V1 → V2 table parity:
      // `sortable` exists so column behavior matches data-table-v1 while we migrate to ag-grid.
      // Can be removed once all tables are using ag-grid and FF is removed
      field,
      sortable,
      headerTooltip,
      cellStyle,
      headerName,
      cellDataType,
      context,
    }) => {
      const tooltipComponent =
        tooltip || headerTooltip ? (
          <Tooltip
            sx={{ fontSize: 17, marginLeft: 1 }}
            title={tooltip || headerTooltip}
          >
            <InfoIcon />
          </Tooltip>
        ) : null;

      const sortEnabled = isSortable || sortable;
      const idNo = id || field;
      const isDateObject =
        objectType || cellDataType === "dateString" ? "date" : null;

      return (
        <TableCell
          key={idNo}
          sortDirection={orderBy === idNo ? order : false}
          sx={{ width: width || cellStyle?.width }}
        >
          {sortEnabled ? (
            <TableSortLabel
              active={orderBy === idNo}
              direction={orderBy === idNo ? order : "asc"}
              onClick={() =>
                handleSort(idNo, isDateObject, onNull || context?.onNull)
              }
            >
              {label || headerName}
              {tooltipComponent}
            </TableSortLabel>
          ) : (
            <Box display="flex" alignItems="center">
              {label || headerName}
              {tooltipComponent}
            </Box>
          )}
        </TableCell>
      );
    },
  );
};

export default TableHeadersSort;
